import { useEffect, useMemo, useState } from 'react';
import { Import, Sparkles } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { PayrollAttendanceInspector } from './PayrollAttendanceInspector';
import { PayrollAttendanceMatrix } from './PayrollAttendanceMatrix';
import { PayrollAttendanceToolbar } from './PayrollAttendanceToolbar';
import {
  buildAllocationDateMap,
  buildDaysInMonth,
  buildIssueDateMap,
  createEmptyAttendanceSegment,
  findApplicableShift,
  formatAttendanceStatusLabel,
  getCellIssues,
  getDefaultDateForPeriod,
  isWorkedAttendanceStatus,
  normalizeAttendanceText,
  resolveAttendanceCellState,
  sortEmployeesForAttendance,
} from './payrollAttendanceUtils';

const emptyAttendanceForm = () => ({
  employee_number: '',
  date: '',
  status: 'presente',
  use_segments: false,
  simple_store_location: '',
  work_start: '08:00',
  lunch_start: '12:00',
  lunch_end: '13:00',
  work_end: '17:00',
  reason: '',
  notes: '',
  segments: [createEmptyAttendanceSegment(), createEmptyAttendanceSegment()],
  swap_request: { original_day: '', actual_worked_day: '', compensatory_day: '', reason: '' },
  swap_approval_notes: '',
});
const emptyTransferForm = () => ({ employee_id: '', employee_number: '', store_location: '', start_date: '', end_date: '', transfer_reason: '', notes: '' });
const emptyShiftForm = () => ({ id: null, store_location: '', employee_number: '', weekday: 'all', shift_name: 'Turno padrão', work_start: '08:00', lunch_start: '12:00', lunch_end: '13:00', work_end: '17:00', tolerance_minutes: 10 });

export const PayrollAttendanceWorkbench = ({
  periodMonth,
  periodYear,
  loading,
  canManagePayroll,
  isManagerReviewer,
  employees,
  locations,
  attendanceRecords,
  attendanceShifts,
  attendanceEmployeeReport,
  attendanceWeeklyRows,
  attendanceClosures,
  justifiedAbsences,
  attendancePendingIssues,
  attendanceValidationIssues,
  employeeStoreAllocations,
  attendanceImportBatches,
  attendanceAggregationPreview,
  attendanceAggregationLoading,
  attendanceSearchQuery,
  setAttendanceSearchQuery,
  attendanceSortField,
  setAttendanceSortField,
  attendanceSortDirection,
  setAttendanceSortDirection,
  attendanceStoreFilter,
  setAttendanceStoreFilter,
  attendanceEmployeeFilter,
  setAttendanceEmployeeFilter,
  attendanceIssueStatusFilter,
  setAttendanceIssueStatusFilter,
  activeAttendanceFiltersCount,
  selectedAttendanceCell,
  setSelectedAttendanceCell,
  attendanceFile,
  setAttendanceFile,
  attendanceImportMode,
  setAttendanceImportMode,
  attendanceUploading,
  onRefresh,
  onSaveAttendance,
  onApproveSwap,
  onAuthorizeOvertime,
  onDeleteAttendance,
  onSaveTransfer,
  onDeleteTransfer,
  onSaveShift,
  onDeleteShift,
  onDownloadTemplate,
  onImportAttendance,
  onClearMonthData,
  onExportXlsx,
  onCloseMonth,
  onReviewClose,
}) => {
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState(emptyAttendanceForm());
  const [transferForm, setTransferForm] = useState(emptyTransferForm());
  const [shiftForm, setShiftForm] = useState(emptyShiftForm());

  const days = useMemo(() => buildDaysInMonth(periodMonth, periodYear), [periodMonth, periodYear]);
  const monthLabel = new Date(periodYear, periodMonth - 1, 1).toLocaleDateString('pt-PT', { month: 'long' });
  const employeeReportMap = useMemo(() => new Map((attendanceEmployeeReport || []).map((row) => [row.employee_number, row])), [attendanceEmployeeReport]);
  const recordMap = useMemo(() => new Map((attendanceRecords || []).map((record) => [`${record.employee_number}::${record.date}`, record])), [attendanceRecords]);
  const absenceMap = useMemo(() => new Map((justifiedAbsences || []).map((absence) => [`${absence.employee_number}::${absence.date}`, absence])), [justifiedAbsences]);
  const allocationDateMap = useMemo(() => buildAllocationDateMap(employeeStoreAllocations || []), [employeeStoreAllocations]);
  const combinedIssues = useMemo(() => ([...(attendancePendingIssues || []), ...(attendanceValidationIssues || [])]), [attendancePendingIssues, attendanceValidationIssues]);
  const issueDateMap = useMemo(() => buildIssueDateMap(combinedIssues), [combinedIssues]);
  const employeeIssuesMap = useMemo(() => {
    const map = new Map();
    combinedIssues.forEach((issue) => {
      if (!issue.employee_number) return;
      const existing = map.get(issue.employee_number) || [];
      map.set(issue.employee_number, [...existing, issue]);
    });
    return map;
  }, [combinedIssues]);
  const employeeTransfersMap = useMemo(() => {
    const map = new Map();
    (employeeStoreAllocations || []).forEach((allocation) => {
      const existing = map.get(allocation.employee_number) || [];
      map.set(allocation.employee_number, [...existing, allocation]);
    });
    return map;
  }, [employeeStoreAllocations]);

  const filteredEmployees = useMemo(() => {
    const query = normalizeAttendanceText(attendanceSearchQuery);
    const baseEmployees = (employees || []).filter((employee) => employee?.employee_number && employee.employee_number !== '000');
    const scopedEmployees = baseEmployees.filter((employee) => {
      const summary = employeeReportMap.get(employee.employee_number) || {};
      const employeeIssues = employeeIssuesMap.get(employee.employee_number) || [];
      if (attendanceStoreFilter !== 'all' && (summary.store_location || employee.store_location) !== attendanceStoreFilter) return false;
      if (attendanceEmployeeFilter !== 'all' && employee.employee_number !== attendanceEmployeeFilter) return false;
      if (attendanceIssueStatusFilter === 'open' && employeeIssues.length === 0) return false;
      if (attendanceIssueStatusFilter === 'error' && !employeeIssues.some((issue) => issue.severity === 'error')) return false;
      if (attendanceIssueStatusFilter === 'warning' && !employeeIssues.some((issue) => issue.severity !== 'error')) return false;
      if (!query) return true;
      return [
        employee.employee_number,
        employee.full_name,
        employee.name,
        employee.store_location,
        summary.store_location,
        ...employeeIssues.map((issue) => issue.message),
      ].some((value) => normalizeAttendanceText(value).includes(query));
    });
    return sortEmployeesForAttendance(scopedEmployees, employeeReportMap, employeeIssuesMap, attendanceSortField, attendanceSortDirection);
  }, [attendanceEmployeeFilter, attendanceIssueStatusFilter, attendanceSearchQuery, attendanceSortDirection, attendanceSortField, attendanceStoreFilter, employeeIssuesMap, employeeReportMap, employees]);

  useEffect(() => {
    if (!filteredEmployees.length) return;
    const isExistingSelectionValid = filteredEmployees.some((employee) => employee.employee_number === selectedAttendanceCell?.employee_number)
      && days.some((day) => day.dateKey === selectedAttendanceCell?.date);
    if (isExistingSelectionValid) return;
    setSelectedAttendanceCell({
      employee_id: filteredEmployees[0].id,
      employee_number: filteredEmployees[0].employee_number,
      date: getDefaultDateForPeriod(periodMonth, periodYear),
    });
  }, [days, filteredEmployees, periodMonth, periodYear, selectedAttendanceCell?.date, selectedAttendanceCell?.employee_number, setSelectedAttendanceCell]);

  const selectedEmployee = useMemo(() => filteredEmployees.find((employee) => employee.employee_number === selectedAttendanceCell?.employee_number) || null, [filteredEmployees, selectedAttendanceCell?.employee_number]);
  const selectedDateKey = selectedAttendanceCell?.date || getDefaultDateForPeriod(periodMonth, periodYear);
  const currentRecord = selectedEmployee ? recordMap.get(`${selectedEmployee.employee_number}::${selectedDateKey}`) || null : null;
  const currentAbsence = selectedEmployee ? absenceMap.get(`${selectedEmployee.employee_number}::${selectedDateKey}`) || null : null;
  const currentAllocation = selectedEmployee ? allocationDateMap.get(`${selectedEmployee.employee_number}::${selectedDateKey}`) || null : null;
  const selectedIssues = useMemo(() => {
    if (!selectedEmployee) return [];
    const cellIssues = getCellIssues({ issueMap: issueDateMap, employeeNumber: selectedEmployee.employee_number, dateKey: selectedDateKey });
    return cellIssues.length ? cellIssues : (employeeIssuesMap.get(selectedEmployee.employee_number) || []);
  }, [employeeIssuesMap, issueDateMap, selectedDateKey, selectedEmployee]);
  const handleIssueAction = (issue) => {
    const issueEmployeeNumber = issue?.employee_number || selectedEmployee?.employee_number || '';
    if (!issueEmployeeNumber) return;

    const issueDate = issue?.issue_data?.date
      || issue?.issue_data?.gap_dates?.[0]
      || selectedDateKey
      || getDefaultDateForPeriod(periodMonth, periodYear);
    const issueEmployee = (employees || []).find((employee) => employee.employee_number === issueEmployeeNumber) || selectedEmployee;

    setAttendanceSearchQuery('');
    setAttendanceStoreFilter('all');
    setAttendanceIssueStatusFilter('all');
    setAttendanceEmployeeFilter(issueEmployeeNumber);
    setSelectedAttendanceCell({
      employee_id: issueEmployee?.id || selectedAttendanceCell?.employee_id || '',
      employee_number: issueEmployeeNumber,
      date: issueDate,
    });
  };
  const currentShift = useMemo(() => {
    if (!selectedEmployee || !selectedDateKey) return null;
    const weekday = new Date(`${selectedDateKey}T00:00:00`).getDay();
    return findApplicableShift({
      shifts: attendanceShifts,
      employeeNumber: selectedEmployee.employee_number,
      storeLocation: currentAllocation?.store_location || currentRecord?.store_location || selectedEmployee.store_location,
      weekday: (weekday + 6) % 7,
    });
  }, [attendanceShifts, currentAllocation?.store_location, currentRecord?.store_location, selectedDateKey, selectedEmployee]);

  useEffect(() => {
    if (!selectedEmployee || !selectedDateKey) return;
    const fallbackSegment = (segment = {}, fallbackStore = '') => ({
      store_location: segment?.store_location || fallbackStore,
      work_start: segment?.work_start || currentShift?.work_start || '08:00',
      lunch_start: segment?.lunch_start || currentShift?.lunch_start || '12:00',
      lunch_end: segment?.lunch_end || currentShift?.lunch_end || '13:00',
      work_end: segment?.work_end || currentShift?.work_end || '17:00',
    });
    const existingSegments = currentRecord?.segments?.length
      ? currentRecord.segments
      : [fallbackSegment({}, currentAllocation?.store_location || selectedEmployee.store_location || '')];
    const paddedSegments = [
      fallbackSegment(existingSegments[0], currentAllocation?.store_location || selectedEmployee.store_location || ''),
      fallbackSegment(existingSegments[1], ''),
    ];
    const resolvedStatus = currentRecord?.status || (currentAbsence ? 'falta_justificada' : 'presente');
    const useSegments = currentRecord?.segment_mode === 'explicit' || Number(currentRecord?.segment_count || 0) > 1;
    setAttendanceForm({
      employee_number: selectedEmployee.employee_number,
      date: selectedDateKey,
      status: resolvedStatus,
      use_segments: useSegments,
      simple_store_location: currentRecord?.store_location || currentAllocation?.store_location || selectedEmployee.store_location || '',
      work_start: currentRecord?.work_start || currentShift?.work_start || '08:00',
      lunch_start: currentRecord?.lunch_start || currentShift?.lunch_start || '12:00',
      lunch_end: currentRecord?.lunch_end || currentShift?.lunch_end || '13:00',
      work_end: currentRecord?.work_end || currentShift?.work_end || '17:00',
      reason: currentRecord?.status_reason || currentAbsence?.reason || '',
      notes: currentRecord?.notes || currentAbsence?.notes || '',
      segments: isWorkedAttendanceStatus(resolvedStatus) ? paddedSegments : [createEmptyAttendanceSegment(), createEmptyAttendanceSegment()],
      swap_request: {
        original_day: currentRecord?.swap_request?.original_day || selectedDateKey,
        actual_worked_day: currentRecord?.swap_request?.actual_worked_day || selectedDateKey,
        compensatory_day: currentRecord?.swap_request?.compensatory_day || '',
        reason: currentRecord?.swap_request?.reason || currentRecord?.status_reason || '',
      },
      swap_approval_notes: currentRecord?.swap_approval?.approval_notes || '',
    });
    setTransferForm({
      employee_id: selectedEmployee.id || '',
      employee_number: selectedEmployee.employee_number,
      store_location: currentAllocation?.store_location || selectedEmployee.store_location || '',
      start_date: currentAllocation?.start_date || selectedDateKey,
      end_date: currentAllocation?.end_date || selectedDateKey,
      transfer_reason: currentAllocation?.transfer_reason || '',
      notes: currentAllocation?.notes || '',
    });
  }, [currentAbsence, currentAllocation, currentRecord, currentShift, selectedDateKey, selectedEmployee]);

  const getCellMeta = (employee, dateKey) => {
    const record = recordMap.get(`${employee.employee_number}::${dateKey}`) || null;
    const absence = absenceMap.get(`${employee.employee_number}::${dateKey}`) || null;
    const allocation = allocationDateMap.get(`${employee.employee_number}::${dateKey}`) || null;
    const issues = getCellIssues({ issueMap: issueDateMap, employeeNumber: employee.employee_number, dateKey });
    const dayMeta = days.find((day) => day.dateKey === dateKey) || null;
    return resolveAttendanceCellState({
      record,
      absence,
      issues,
      allocation,
      employeeBaseStore: employee.store_location,
      dayMeta,
    });
  };

  const selectedCellState = selectedEmployee ? getCellMeta(selectedEmployee, selectedDateKey) : null;
  const saveActionLabel = `Guardar ${formatAttendanceStatusLabel(attendanceForm.status || 'presente').toLowerCase()}`;

  return (
    <div className="space-y-4" data-testid="payroll-attendance-page">
      <PayrollAttendanceToolbar
        monthLabel={monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}
        year={periodYear}
        loading={loading}
        canManagePayroll={canManagePayroll}
        attendanceSearchQuery={attendanceSearchQuery}
        setAttendanceSearchQuery={setAttendanceSearchQuery}
        attendanceSortField={attendanceSortField}
        setAttendanceSortField={setAttendanceSortField}
        attendanceSortDirection={attendanceSortDirection}
        setAttendanceSortDirection={setAttendanceSortDirection}
        attendanceStoreFilter={attendanceStoreFilter}
        setAttendanceStoreFilter={setAttendanceStoreFilter}
        attendanceEmployeeFilter={attendanceEmployeeFilter}
        setAttendanceEmployeeFilter={setAttendanceEmployeeFilter}
        attendanceIssueStatusFilter={attendanceIssueStatusFilter}
        setAttendanceIssueStatusFilter={setAttendanceIssueStatusFilter}
        activeAttendanceFiltersCount={activeAttendanceFiltersCount}
        locations={locations}
        employees={filteredEmployees}
        pendingCount={attendancePendingIssues.length}
        validationCount={attendanceValidationIssues.length}
        batchCount={attendanceImportBatches.length}
        attendanceFile={attendanceFile}
        setAttendanceFile={setAttendanceFile}
        attendanceImportMode={attendanceImportMode}
        setAttendanceImportMode={setAttendanceImportMode}
        attendanceUploading={attendanceUploading}
        onRefresh={onRefresh}
        onDownloadTemplate={onDownloadTemplate}
        onImportAttendance={onImportAttendance}
        onClearMonthData={onClearMonthData}
        onExportXlsx={onExportXlsx}
        onSaveCurrent={() => {
          const simpleSegments = attendanceForm.use_segments ? attendanceForm.segments : [{
            store_location: attendanceForm.simple_store_location || currentAllocation?.store_location || selectedEmployee?.store_location || '',
            work_start: attendanceForm.work_start,
            lunch_start: attendanceForm.lunch_start,
            lunch_end: attendanceForm.lunch_end,
            work_end: attendanceForm.work_end,
          }];
          onSaveAttendance({
            ...attendanceForm,
            segment_mode: attendanceForm.use_segments ? 'explicit' : 'simple',
            segments: attendanceForm.use_segments ? attendanceForm.segments : simpleSegments,
          }, currentRecord?.id || null);
        }}
        onOpenShiftDialog={() => setShiftDialogOpen(true)}
        saveActionLabel={saveActionLabel}
      />

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-4">
          <PayrollAttendanceMatrix
            days={days}
            employees={filteredEmployees}
            selectedAttendanceCell={selectedAttendanceCell}
            onSelectCell={setSelectedAttendanceCell}
            employeeSummaries={new Map(filteredEmployees.map((employee) => {
              const summary = employeeReportMap.get(employee.employee_number) || {};
              const issues = employeeIssuesMap.get(employee.employee_number) || [];
              return [employee.employee_number, { ...summary, issue_count: issues.length }];
            }))}
            employeeTransfers={employeeTransfersMap}
            getCellMeta={getCellMeta}
          />

          <div className="grid gap-3 xl:grid-cols-3">
            <Card className="rounded-md border-slate-200 shadow-sm" data-testid="payroll-attendance-summary-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Resumo RH</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-0 text-sm">
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                  {(attendanceEmployeeReport || []).slice(0, 4).map((row) => (
                    <div key={row.employee_number} className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`payroll-attendance-summary-employee-${row.employee_number}`}>
                      <p className="font-semibold text-slate-900">{row.employee_number} · {row.employee_name}</p>
                      <p className="text-xs text-slate-600">{row.store_location || 'Sem loja'} · {row.total_hours || 0}h · HE aut. {row.authorized_overtime_hours || 0}h · HE pend. {row.pending_overtime_hours || 0}h</p>
                      <p className="text-xs text-slate-500">Feriados {row.holiday_count || 0} · Feriados trabalhados {row.holiday_worked_count || 0}</p>
                    </div>
                  ))}
                </div>
                {(attendanceWeeklyRows || []).slice(0, 1).map((row) => (
                  <div key={`${row.employee_number}-${row.week_start || 'week'}`} className="rounded-sm border border-blue-200 bg-blue-50 px-3 py-2" data-testid="payroll-attendance-summary-weekly-map">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">Mapa semanal</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{row.employee_number} · {row.employee_name}</p>
                    <p className="text-xs text-slate-600">Banco {row.bank_hours || 0}h · {row.days?.length || 0} dias</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-md border-slate-200 shadow-sm" data-testid="payroll-attendance-import-batches-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm"><Import className="h-4 w-4" /> Lotes & importação</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-0 text-sm">
                {attendanceImportBatches.length === 0 ? (
                  <p className="text-slate-500">Sem batches recentes para este módulo.</p>
                ) : attendanceImportBatches.slice(0, 4).map((batch) => (
                  <div key={batch.id} className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`payroll-attendance-batch-${batch.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-slate-900">{batch.batch_number || batch.id}</p>
                      <Badge variant="outline" className="rounded-sm bg-white">{batch.status || 'draft'}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-slate-600">Importados {batch.summary?.imported || 0} · pendentes {batch.summary?.pending_issues || 0}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-md border-slate-200 shadow-sm" data-testid="payroll-attendance-governance-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm"><Sparkles className="h-4 w-4" /> Fecho & alertas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-0 text-sm">
                <Button type="button" size="sm" className="h-8 rounded-sm bg-slate-950 text-white hover:bg-slate-800" onClick={onCloseMonth} disabled={!isManagerReviewer} data-testid="payroll-attendance-close-month-button">
                  Fechar mês actual
                </Button>
                <Button type="button" size="sm" variant="outline" className="h-8 rounded-sm border-red-200 text-red-700 hover:bg-red-50" onClick={onClearMonthData} disabled={!canManagePayroll} data-testid="payroll-attendance-governance-clear-month-button">
                  Limpar dados do mês
                </Button>
                {attendanceClosures.slice(0, 3).map((closure) => (
                  <div key={closure.id} className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`payroll-attendance-closure-${closure.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold text-slate-900">{String(closure.month).padStart(2, '0')}/{closure.year}</p>
                        <p className="text-xs text-slate-600">{closure.status || 'draft'} · banco {closure.summary?.closing_bank_hours || 0}h</p>
                      </div>
                      <div className="flex gap-1">
                        <Button type="button" variant="outline" size="sm" className="h-7 rounded-sm px-2 text-xs" onClick={() => onReviewClose(closure.id, 'approve')} disabled={!isManagerReviewer} data-testid={`payroll-attendance-closure-approve-${closure.id}`}>Aprovar</Button>
                        <Button type="button" variant="outline" size="sm" className="h-7 rounded-sm px-2 text-xs" onClick={() => onReviewClose(closure.id, 'reopen')} disabled={!isManagerReviewer} data-testid={`payroll-attendance-closure-reopen-${closure.id}`}>Reabrir</Button>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="rounded-sm border border-orange-200 bg-orange-50 px-3 py-2" data-testid="payroll-attendance-governance-alert-box">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-700">Pendência operacional</p>
                  <p className="mt-1 text-sm text-orange-900">{attendancePendingIssues.length + attendanceValidationIssues.length} itens abertos exigem validação antes do fecho definitivo.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <PayrollAttendanceInspector
          selectedEmployee={selectedEmployee}
          selectedDateKey={selectedDateKey}
          selectedCellState={selectedCellState}
          attendanceForm={attendanceForm}
          setAttendanceForm={setAttendanceForm}
          transferForm={transferForm}
          setTransferForm={setTransferForm}
          currentRecord={currentRecord}
          currentAbsence={currentAbsence}
          currentShift={currentShift}
          currentAllocation={currentAllocation}
          employeeAllocations={employeeTransfersMap.get(selectedEmployee?.employee_number) || []}
          selectedIssues={selectedIssues}
          aggregationPreview={attendanceAggregationPreview}
          aggregationLoading={attendanceAggregationLoading}
          locations={locations}
          canManagePayroll={canManagePayroll}
          canApproveSwap={isManagerReviewer}
          onIssueAction={handleIssueAction}
          onSaveAttendance={(payload, recordId) => onSaveAttendance({
            ...payload,
            segment_mode: payload.use_segments ? 'explicit' : 'simple',
            segments: payload.use_segments ? payload.segments : [{
              store_location: payload.simple_store_location || currentAllocation?.store_location || selectedEmployee?.store_location || '',
              work_start: payload.work_start,
              lunch_start: payload.lunch_start,
              lunch_end: payload.lunch_end,
              work_end: payload.work_end,
            }],
          }, recordId)}
          onApproveSwap={onApproveSwap}
          onAuthorizeOvertime={onAuthorizeOvertime}
          onDeleteAttendance={onDeleteAttendance}
          onSaveTransfer={onSaveTransfer}
          onDeleteTransfer={onDeleteTransfer}
        />
      </div>

      <Dialog open={shiftDialogOpen} onOpenChange={setShiftDialogOpen}>
        <DialogContent className="w-[min(96vw,860px)] max-w-[860px] rounded-sm" data-testid="payroll-attendance-shift-dialog">
          <DialogHeader>
            <DialogTitle>Turnos rotativos</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1">
              <Label>Loja</Label>
              <Select value={shiftForm.store_location || 'none'} onValueChange={(value) => setShiftForm((current) => ({ ...current, store_location: value === 'none' ? '' : value }))}>
                <SelectTrigger data-testid="payroll-attendance-shift-store-select"><SelectValue placeholder="Seleccionar loja" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Seleccionar</SelectItem>
                  {locations.filter((location) => (location?.type ? location.type === 'store' : true) && location?.is_active !== false).map((location) => (
                    <SelectItem key={location.id || location.name} value={location.name}>{location.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Nº colaborador</Label><Input value={shiftForm.employee_number} onChange={(event) => setShiftForm((current) => ({ ...current, employee_number: event.target.value }))} data-testid="payroll-attendance-shift-employee-input" /></div>
            <div className="space-y-1"><Label>Dia</Label><Select value={shiftForm.weekday} onValueChange={(value) => setShiftForm((current) => ({ ...current, weekday: value }))}><SelectTrigger data-testid="payroll-attendance-shift-weekday-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="0">Seg</SelectItem><SelectItem value="1">Ter</SelectItem><SelectItem value="2">Qua</SelectItem><SelectItem value="3">Qui</SelectItem><SelectItem value="4">Sex</SelectItem><SelectItem value="5">Sáb</SelectItem><SelectItem value="6">Dom</SelectItem></SelectContent></Select></div>
            <div className="space-y-1"><Label>Nome</Label><Input value={shiftForm.shift_name} onChange={(event) => setShiftForm((current) => ({ ...current, shift_name: event.target.value }))} data-testid="payroll-attendance-shift-name-input" /></div>
            <div className="space-y-1"><Label>Entrada</Label><Input type="time" value={shiftForm.work_start} onChange={(event) => setShiftForm((current) => ({ ...current, work_start: event.target.value }))} data-testid="payroll-attendance-shift-work-start" /></div>
            <div className="space-y-1"><Label>Saída almoço</Label><Input type="time" value={shiftForm.lunch_start} onChange={(event) => setShiftForm((current) => ({ ...current, lunch_start: event.target.value }))} data-testid="payroll-attendance-shift-lunch-start" /></div>
            <div className="space-y-1"><Label>Entrada almoço</Label><Input type="time" value={shiftForm.lunch_end} onChange={(event) => setShiftForm((current) => ({ ...current, lunch_end: event.target.value }))} data-testid="payroll-attendance-shift-lunch-end" /></div>
            <div className="space-y-1"><Label>Saída</Label><Input type="time" value={shiftForm.work_end} onChange={(event) => setShiftForm((current) => ({ ...current, work_end: event.target.value }))} data-testid="payroll-attendance-shift-work-end" /></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" className="rounded-sm bg-slate-950 text-white hover:bg-slate-800" onClick={() => onSaveShift(shiftForm)} data-testid="payroll-attendance-shift-save-button">Guardar turno</Button>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setShiftForm(emptyShiftForm())} data-testid="payroll-attendance-shift-reset-button">Limpar</Button>
          </div>
          <div className="space-y-2">
            {attendanceShifts.slice(0, 8).map((shift) => (
              <div key={shift.id || `${shift.store_location}-${shift.employee_number || 'all'}-${shift.weekday ?? 'all'}`} className="flex items-center justify-between gap-2 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`payroll-attendance-shift-row-${shift.id || `${shift.store_location}-${shift.employee_number || 'all'}-${shift.weekday ?? 'all'}`}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{shift.shift_name || 'Turno'} · {shift.store_location}</p>
                  <p className="text-xs text-slate-600">{shift.employee_number || 'Todos'} · {shift.work_start} / {shift.lunch_start} / {shift.lunch_end} / {shift.work_end}</p>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" className="h-8 rounded-sm px-2 text-xs" onClick={() => setShiftForm({ id: shift.id, store_location: shift.store_location, employee_number: shift.employee_number || '', weekday: shift.weekday ?? 'all', shift_name: shift.shift_name || 'Turno padrão', work_start: shift.work_start, lunch_start: shift.lunch_start, lunch_end: shift.lunch_end, work_end: shift.work_end, tolerance_minutes: shift.tolerance_minutes || 10 })} data-testid={`payroll-attendance-shift-edit-${shift.id}`}>Editar</Button>
                  <Button type="button" variant="outline" size="sm" className="h-8 rounded-sm border-red-200 px-2 text-xs text-red-700 hover:bg-red-50" onClick={() => onDeleteShift(shift.id)} data-testid={`payroll-attendance-shift-delete-${shift.id}`}>Remover</Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};